import { useState, useRef } from "react";
import { Share2, Copy, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { trackEvent } from "@/lib/analytics";

/**
 * ShareButton — CondominioNow.
 *
 * Prefers `navigator.share()` (opens the native share sheet on iOS/Android
 * and modern desktop Chromium). Falls back to a small Popover with three
 * options: Copia link, WhatsApp, Email. No promotional dark patterns, no
 * gamification. Toast confirmation after every successful action.
 *
 * Props
 *   payload  { kind: "app"|"faq", title, text, url }
 *   variant  "hero-blue" | "inline" | "compact"
 *   label    visible text on the button (default "Condividi")
 *
 * Accessibility: 44px min touch target, focus-visible ring, aria-label,
 * respects prefers-reduced-motion via Tailwind `motion-safe:` utilities where
 * animations are added by consumers.
 */
export function ShareButton({
    payload,
    variant = "inline",
    label = "Condividi",
    className = "",
    "data-testid": dataTestid,
}) {
    const [fallbackOpen, setFallbackOpen] = useState(false);
    const triggerRef = useRef(null);
    const eventName = payload?.kind === "faq" ? "FAQ_SHARED" : "APP_SHARED";

    const openNativeOrFallback = async () => {
        const { title, text, url } = payload || {};
        if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
            const canShare = typeof navigator.canShare === "function"
                ? navigator.canShare({ title, text, url })
                : true;
            if (canShare) {
                try {
                    await navigator.share({ title, text, url });
                    trackEvent(eventName, { via: "native", kind: payload.kind });
                    toast.success("Grazie per aver condiviso");
                    return;
                } catch (e) {
                    if (e && e.name === "AbortError") return; // user cancelled → silent
                    // any other error → fall through to popover
                }
            }
        }
        setFallbackOpen(true);
    };

    const copyLink = async () => {
        const { url, text } = payload || {};
        const clipboardText = text ? `${text}\n${url}` : url;
        try {
            await navigator.clipboard.writeText(clipboardText);
        } catch {
            // legacy fallback
            const ta = document.createElement("textarea");
            ta.value = clipboardText;
            ta.setAttribute("readonly", "");
            ta.style.position = "absolute";
            ta.style.left = "-9999px";
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch { /* noop */ }
            document.body.removeChild(ta);
        }
        trackEvent(eventName, { via: "copy", kind: payload.kind });
        toast.success("Link copiato negli appunti");
        setFallbackOpen(false);
    };

    const shareWhatsApp = () => {
        const { text, url } = payload || {};
        const msg = encodeURIComponent(`${text}\n${url}`);
        window.open(`https://wa.me/?text=${msg}`, "_blank", "noopener,noreferrer");
        trackEvent(eventName, { via: "whatsapp", kind: payload.kind });
        toast.success("Apertura WhatsApp");
        setFallbackOpen(false);
    };

    const shareEmail = () => {
        const { title, text, url } = payload || {};
        const subject = encodeURIComponent(title || "CondominioNow");
        const body = encodeURIComponent(`${text || ""}\n\n${url}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        trackEvent(eventName, { via: "email", kind: payload.kind });
        toast.success("Apertura client email");
        setFallbackOpen(false);
    };

    const styles = {
        "hero-blue":
            "inline-flex items-center justify-center gap-2 rounded-full h-11 sm:h-12 px-5 sm:px-6 text-[15px] font-semibold border-2 border-white/70 text-white bg-transparent hover:bg-white hover:text-primary transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40 min-h-[44px]",
        "accent-lg":
            "inline-flex items-center justify-center gap-2.5 rounded-full h-13 sm:h-14 px-7 sm:px-9 text-[16px] sm:text-[17px] font-bold bg-accent text-white hover:bg-[#E17F2C] active:scale-[0.98] shadow-[0_12px_28px_-10px_rgba(245,149,71,0.65)] hover:shadow-[0_16px_32px_-10px_rgba(245,149,71,0.75)] transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent/45 min-h-[52px]",
        "inline":
            "inline-flex items-center justify-center gap-2 rounded-full h-11 px-5 text-[14.5px] font-semibold border-2 border-primary text-primary bg-white hover:bg-primary hover:text-white transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/25 min-h-[44px]",
        "compact":
            "inline-flex items-center gap-2 rounded-full px-4 h-11 text-sm font-semibold text-primary bg-primary/[0.06] border border-primary/25 hover:bg-primary/12 transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/25 min-h-[44px]",
    }[variant] || "";

    return (
        <Popover open={fallbackOpen} onOpenChange={setFallbackOpen}>
            <PopoverTrigger asChild>
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={openNativeOrFallback}
                    aria-label={label}
                    data-testid={dataTestid || "share-button"}
                    className={`${styles} ${className}`}
                >
                    <Share2 className="size-4" aria-hidden strokeWidth={2.2} />
                    <span>{label}</span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="center"
                sideOffset={8}
                className="w-56 p-1.5"
                data-testid="share-fallback-popover"
            >
                <button
                    type="button"
                    onClick={copyLink}
                    data-testid="share-copy"
                    className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-primary/10 focus-visible:outline-none focus-visible:bg-primary/10 transition min-h-[44px]"
                >
                    <Copy className="size-4 text-primary" aria-hidden />
                    <span>Copia link</span>
                </button>
                <button
                    type="button"
                    onClick={shareWhatsApp}
                    data-testid="share-whatsapp"
                    className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-primary/10 focus-visible:outline-none focus-visible:bg-primary/10 transition min-h-[44px]"
                >
                    <MessageCircle className="size-4 text-primary" aria-hidden />
                    <span>WhatsApp</span>
                </button>
                <button
                    type="button"
                    onClick={shareEmail}
                    data-testid="share-email"
                    className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-primary/10 focus-visible:outline-none focus-visible:bg-primary/10 transition min-h-[44px]"
                >
                    <Mail className="size-4 text-primary" aria-hidden />
                    <span>Email</span>
                </button>
            </PopoverContent>
        </Popover>
    );
}

/**
 * Ready-made payload builders — keep copy consistent across the app.
 * SITE URL is intentionally hardcoded to the production domain so links
 * shared from any environment always resolve to the live site.
 */
export const SITE_URL = "https://condominionow.it";

export function appSharePayload() {
    return {
        kind: "app",
        title: "CondominioNow",
        text:
            "Hai un dubbio sul tuo condominio?\n" +
            "Trova risposte chiare e informazioni utili sui principali problemi della vita condominiale.",
        url: `${SITE_URL}/`,
    };
}

export function faqSharePayload(faq) {
    const title = faq?.title || "CondominioNow";
    // Deep-link via query string on the public /faq page; Faqs.jsx reads
    // `?faq=<id>` on mount to auto-open the correct accordion and scroll.
    const id = faq?.id ? encodeURIComponent(faq.id) : "";
    const url = id ? `${SITE_URL}/faq?faq=${id}` : `${SITE_URL}/faq`;
    return {
        kind: "faq",
        title,
        text: "Ho trovato questa risposta su CondominioNow.",
        url,
    };
}

/**
 * Payload for the three "Guide Rapide" landing pages.
 *   guide = { path: "/faq/ripartizione-spese", title: "Ripartizione spese..." }
 * The shared URL points to the guide's dedicated page.
 */
export function guideSharePayload(guide) {
    const title = guide?.title || "CondominioNow";
    const path = guide?.path || "/";
    const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
    return {
        kind: "app",
        title,
        text:
            "Conosci qualcuno con un problema condominiale?\n" +
            "Condividi CondominioNow e aiutalo a trovare la risposta che cerca.",
        url,
    };
}

export default ShareButton;
